/**
 * Rótulos amigáveis (PT-BR) para as ferramentas do @Atendente e do @DevOps,
 * usados no streaming ao vivo dos passos ("acompanhar o raciocínio").
 * Ferramenta desconhecida cai num rótulo genérico legível (sem underscore cru).
 *
 * Toda ferramenta registrada no backend (`assistant_tools._HANDLERS` e
 * `TOOLS`) tem de ter rótulo aqui: quem confere é
 * `tests/unit/test_diagnostics_consulta_repasse_de_passos.py`, que lê este
 * arquivo (spec 2026-10-09 §4 F1.1 — "Consultando consultar devops" no bs-hub).
 * Mantenha uma entrada por linha, no formato `nome: 'Rótulo'`.
 */
const LABELS: Record<string, string> = {
  list_resources: 'Consultando recursos',
  get_costs: 'Analisando custos',
  get_cost_summary: 'Analisando custos',
  get_dns_records: 'Consultando registros DNS',
  get_health_alarms: 'Verificando alarmes e saúde',
  get_instance_network: 'Verificando a rede da instância',
  get_recommendations: 'Buscando recomendações',
  get_security_findings: 'Verificando achados de segurança',
  get_security_changes: 'Verificando o que mudou na segurança',
  get_operacao_findings: 'Verificando os sinais de operação da AWS',
  get_pentest_recon: 'Verificando caminhos de escalada de privilégio',
  get_server_security_questionnaire: 'Consultando questionário de segurança',
  get_tenant_config: 'Consultando a configuração do tenant',
  get_ztna_status: 'Verificando o status do ZTNA',
  get_consoleai_features: 'Consultando funcionalidades do ConsoleAI',
  get_route53: 'Consultando as zonas de DNS',
  search_knowledge: 'Buscando na base de conhecimento',
  inventariar_pasta_kb: 'Listando a pasta da base de conhecimento',
  propose_action: 'Preparando sua solicitação (chamado/ação)…',
  consultar_devops: 'Estou analisando junto com o @DevOps',
  consultar_dba: 'Estou analisando junto com o @DBA',
  // Leituras do @DBA (passos de dentro de `consultar_dba`, spec 2026-10-30-agente-dba §4.5).
  'banco.colunas': 'Lendo as colunas e os tipos da tabela',
  'banco.rotulos_de_enum': 'Lendo os valores que o enum aceita',
  'banco.existe': 'Procurando o objeto no banco',
  'banco.restricoes': 'Lendo as chaves da tabela',
  'banco.indices': 'Lendo os índices da tabela',
  'banco.explicar_plano': 'Pedindo ao banco o plano da consulta, sem executá-la',
  anotar_requisito_de_ambiente: 'Anotando no rascunho do ambiente',
  ler_rascunho_do_ambiente: 'Lendo o rascunho',
  descartar_rascunho_de_ambiente: 'Descartando o rascunho do ambiente',
  derivar_ambiente: 'Montando o ambiente a partir de outro já criado',
  redigir_ajuste_terraform: 'Redigindo o ajuste de infraestrutura',
  estado_do_stage: 'Lendo o estado do stage',
  diagnosticar_dominio: 'Verificando o domínio',
  diagnosticar_saude_de_instancia: 'Verificando a saúde do servidor',
  check_server_status: 'Verificando o servidor',
  list_dns: 'Consultando registros DNS',
  list_load_balancers: 'Verificando os balanceadores de carga',
  list_vpc_network: 'Verificando a rede (VPC)',
  list_vpn: 'Verificando as conexões VPN',
  list_waf: 'Verificando o WAF',
  avaliar_recomendacao_waf: 'Avaliando a recomendação do WAF',
  list_instance_files: 'Listando arquivos no servidor',
  read_instance_file: 'Lendo um arquivo no servidor',
  run_read_command: 'Rodando um comando de leitura no servidor',
  run_host_order: 'Preparando a ação no servidor',
  list_chamados: 'Consultando chamados',
  list_chamado_categorias: 'Consultando as categorias de chamado',
  list_my_tenants: 'Consultando seus ambientes',
  generate_report: 'Gerando o relatório',
  preencher_questionario: 'Preenchendo o questionário',
  acionar_robo: 'Acionando o robô',
}

/** Os nomes com rótulo próprio — para o teste de legibilidade. */
export const FERRAMENTAS_COM_ROTULO: readonly string[] = Object.keys(LABELS)

export function friendlyToolLabel(name: string): string {
  if (LABELS[name]) return LABELS[name]
  const readable = name.replace(/_/g, ' ').trim()
  return `Consultando ${readable}`
}

/**
 * Os especialistas atrás do @Atendente: a porta (ferramenta que o @Atendente chama), o nome
 * com que a bolha agrupa os passos de dentro e a linha de quando a consulta terminou (spec
 * 2026-10-09 §4 F1.1; o @DBA, spec 2026-10-30-agente-dba §4.2).
 */
export const ESPECIALISTAS = {
  devops: { nome: '@DevOps', porta: 'consultar_devops', respondeu: 'O @DevOps respondeu' },
  dba: { nome: '@DBA', porta: 'consultar_dba', respondeu: 'O @DBA respondeu' },
} as const

export type Especialista = keyof typeof ESPECIALISTAS

export function eEspecialista(agente: unknown): agente is Especialista {
  return typeof agente === 'string' && Object.prototype.hasOwnProperty.call(ESPECIALISTAS, agente)
}

/** "O @X respondeu" quando `nome` é a porta de um especialista; senão `null`. */
export function respondeuDaPorta(nome: string): string | null {
  const e = Object.values(ESPECIALISTAS).find((x) => x.porta === nome)
  return e ? e.respondeu : null
}
